import {arrayEquals, randomId} from '../../../util/CommonUtils.js';
import {LinearScale, OrdinalScale} from './Scales.js';
import {SelectionHelper} from '../VisualizationDataSelection.js';
import Area from "../../../svg-wrapper/Area.js";
import AreaSelector from "./AreaSelector.js";
import {safeHtml} from "../../../util/CommonUtils.js";

export class ChartComponent {
	#callbacks = {
		onSelectionChanged: [],
	}
	constructor(parentNode, props, selection, chartDataBuilder) {
		this._properties = new ChartProperties(props);
		this._root = this._render(parentNode);
		this._scale = new ChartScale(this._properties)
		this._axes = new Axes(this._root.querySelector('svg'), this._scale);
		this._pointsDrawer = new Drawer(this._root.querySelector('.data-layer'), this._scale);
		this._statisticDrawer = new Drawer(this._root.querySelector('.statistics-layer'), this._scale);
		this._areaSelector = new AreaSelector(this._root.querySelector('.area-selector'), this._scale);
		this._pointSelector = new PointsSelector(this, chartDataBuilder.vizData, selection);
		this._chartPointTooltip = new ChartPointTooltipComponent(this._root);
		new ResizeableChartComponent(parentNode, this);
		this._properties.addOnChangeListeners(this);
		this._chartDataBuilder = chartDataBuilder;
		this._areaSelector.on('start', (selection, event)=>{
			this._pointSelector._onSelectionStarted(selection, event)
		})
		this._areaSelector.on('select', (selection, event)=>{
			this._pointSelector._onSelecting(selection, event)
		})
		this._areaSelector.on('end', (selection, event)=>{
			this._pointSelector._onSelectionEnd(selection, event)
		});
		this._pointSelector.onSelectionChanged((data)=>{
			this.#callbacks.onSelectionChanged.forEach(callback => callback(data))
		})
	}

	resize(width, height) {
		this._properties.set({'size.width':width, 'size.height': height})
	}

	updateData(points){
		this._pointsDrawer.clean();
		this._scale._x = ScaleFactory.create(points.x.values, this._scale.x.range, points.x.type);
		this._scale._y = ScaleFactory.create(points.y.values, this._scale.y.range, points.y.type);
		const circles = [];
		for (let i = 0; i < points.x.values.length; i++){
			let circleRadius = points.size.values[i];
			if (circleRadius === undefined)
				continue;
			let circle = this._pointsDrawer.drawCircle(
				{x: points.x.values[i], y: points.y.values[i]}, 'scatter-plot-circle',
				{id: points.ids[i], r: circleRadius, color: points.color.values[i], selected: this._pointSelector.isSelected(points.ids[i])}
			);
			circles.push(circle);
			circle.element.on('mouseover', () => {
				this._chartPointTooltip.show(circle.getBoundingClientRect(), this._getTooltipData(circle.element.getAttr('id')));
			})
			circle.element.on('mouseout', () => {
				this._chartPointTooltip.hide();
			})
			circle.element.on('click', /**@param {PointerEvent} event*/(event) => {
				this._pointSelector.updateSelection([circle], event)
				this._pointSelector._onSelectionEnd()
			})
		}
		for (const circle of circles.sort((a, b) => +b.element.getAttr('r') - +a.element.getAttr('r')))
			circle.element.asLastChild();
		for (const entry of points.connections) {
			const p = [], c = [];
			for (const id of entry[1]) {
				let circ = circles.find(c=>+c.element.getAttr('id') === id);
				if (circ){
					p.push([circ.coords.x, circ.coords.y])
					c.push(circ);
				}
			}
			p.sort((a,b)=>a[1] > b[1] ? 1 : a[1] < b[1]? -1 : a[0] > b[0] ? 1 : a[0] < b[0] ? -1 : 0)
			let connection = this._pointsDrawer.drawPath(p, 'connection');
			connection.element.asFirstChild();
			connection.dataset = c;
			connection.element.on('click', ()=>{
				this._pointSelector.updateSelection(connection.dataset, {})
				this._pointSelector._onSelectionEnd()
			})
		}
		this._axes.rescale()
		this._pointsDrawer.onScaleChanged();
	}

	/**
	 *  Called every time when we change properties
	 *  dumb function that updates all chart, need to update only changed parameters, TBD later
	 **/
	onPropertiesChanged() {
		const marginLeft = this._properties.get('margin.left');
		const marginTop = this._properties.get('margin.top');
		const xAxisPadding = this._properties.get('size.height') - this._properties.get('margin.bottom') - marginTop;

		this._scale.setRange(this._properties.get('axes.x.range'), this._properties.get('axes.y.range'))

		this._root.querySelector('.groups').setAttribute('transform', `translate(${marginLeft},${marginTop})`)
		this._root.querySelector('.axis-x').setAttribute('transform', `translate(0, ${xAxisPadding})`)

		this._axes.rescale()

		this._pointsDrawer.onScaleChanged();
		this._statisticDrawer.onScaleChanged();
		this._areaSelector.onScaleChanged();
	}

	onSelectionChanged(cb) {
		this.#callbacks.onSelectionChanged.push(cb);
	}

	_getTooltipData(rowId) {
		let result = this._chartDataBuilder.getPointMeta(rowId);
		const axes = [result.x.name, result.y.name]
		const rows = [['Injection', ':', result.injection]];
		if (result.substance !== null)
			rows.push(['Substance', ':', result.substance])
		else if (result.base !== null)
			rows.push(['Base', ':', result.base])
		const sizeName = result.size.name, colorName = result.color.name;
		if (sizeName !== null && !axes.includes(sizeName))
			rows.push([`Size(${sizeName})`, ':', result.size.value])
		if (colorName !== null && !axes.includes(colorName))
			rows.push([`Color(${colorName})`, ':', result.color.value])
		rows.push([result.y.name, ':', result.y.value])
		rows.push([result.x.name, ':', result.x.value])
		return rows
	}

	_render(parentNode) {
		const marginLeft = this._properties.get('margin.left');
		const marginTop = this._properties.get('margin.top');
		const xAxisPadding = this._properties.get('size.height') - this._properties.get('margin.bottom') - marginTop;
		let template = document.createElement('template');
		template.innerHTML = `<div class="chart-component">
				  				<svg width="100%" height="100%">
				  				  <g class="groups" transform="translate(${marginLeft},${marginTop})">
				  				  	<g class="axes">
						     	      <g class="axis-x" transform="translate(0,${xAxisPadding})"></g>
								      <g class="axis-y"></g>
							      	</g>
							      	<g class="layers">
							      	  <g class="area-selector"></g>
								  	  <g class="statistics-layer"></g>
								  	  <g class="data-layer"></g>
								  	</g>
								  </g>
								</svg>
							  </div>`
		parentNode.appendChild(template.content.cloneNode(true))
		return parentNode.lastChild
	}
}

export class ChartScale {
	constructor(props) {
		this._x = ScaleFactory.create(props.get('axes.x.domain'), props.get('axes.x.range'));
		this._y = ScaleFactory.create(props.get('axes.y.domain'), props.get('axes.y.range'));
	}

	setRange(xRange, yRange) {
		this.x.range = xRange.slice();
		this.y.range = yRange.slice();
	}

	setDomain(xDomain, yDomain) {
		if (!arrayEquals(xDomain, this.x.domain))
			this._x = ScaleFactory.create(xDomain, this._x.range);
		if (!arrayEquals(yDomain, this.y.domain))
			this._y = ScaleFactory.create(yDomain, this._y.range);
	}

	get x() {
		return this._x;
	}

	get y() {
		return this._y;
	}
}

export class ChartProperties {
	constructor(props) {
		this._staticProps = {
			'axes.x.range.lower': 0,
			'axes.x.domain': [0, 100],
			'axes.y.range.upper': 0,
			'axes.y.domain': [0, 100],
			'margin.top': 0,
			'margin.right': 20,
			'margin.bottom': 20,
			'margin.left': 30,
			'size.width': 300,
			'size.height': 300
		}
		this._computedProps = {
			'axes.x.range.upper': () => {
				return this.get('size.width') - this.get('margin.left') - this.get('margin.right')
			},
			'axes.y.range.lower': () => {
				return this.get('size.height') - this.get('margin.top') - this.get('margin.bottom')
			},
			'axes.x.range': () => {
				return [this.get('axes.x.range.lower'),this.get('axes.x.range.upper')]
			},
			'axes.y.range': () => {
				return [this.get('axes.y.range.lower'), this.get('axes.y.range.upper')]
			}
		}
		this._onChangeListeners = [];
		this.set(props)
	}

	get(name) {
		if (this._staticProps.hasOwnProperty(name))
			return this._staticProps[name]
		let computedProp = this._computedProps[name];
		if (typeof computedProp === 'function')
			return computedProp();
		throw new Error(`Can't find property[${name}]`)
	}

	set(props = {}) {
		for (const key in props) {
			const val = props[key];
			if (this._staticProps.hasOwnProperty(key) && typeof val !== 'function')
				this._staticProps[key] = val
		}
		this._notifyAll(props)
	}

	addOnChangeListeners(...listeners) {
		this._onChangeListeners.push(...listeners);
	}

	_notifyAll(changes) {
		for (const onChangeListener of this._onChangeListeners) {
			onChangeListener.onPropertiesChanged(changes)
		}
	}
}

export class ScaleFactory {
	static create(domain, range, domainType) {
		if (domainType === undefined)
			domainType = ScaleUtil.resolveDomainType(domain)
		switch (domainType) {
			case 'number' : {
				return new LinearScale({range:range, domain: domain, padding: 0.05});
			}
			default : {
				if (Array.isArray(domain) && domain.length === 0)
					domain = [null]
				return new OrdinalScale({range:range, domain: domain, padding: 0.5});
			}
		}
	}
}

class ScaleUtil {
	static resolveDomainType(domain){
		if (Array.isArray(domain)){
			for (const el of domain) {
				if (typeof el === "number" && isFinite(el))
					return "number";
				else if (typeof el === "string" && el.length > 0)
					return "string"
			}
		}
		return "string"
	}
}

export class Drawer {
	constructor(drawLayer, scale) {
		this._area = new Area(drawLayer);
		this._scale = scale;
		/** @type {Drawable[]} */
		this._elements = [];
	}

	clean() {
		while (this._elements.length > 0)
			this._elements.shift().remove()
	}

	drawCircle(coordinates, cssClass, attributes = {}) {
		let circle = new CircleElement(this._area, coordinates, this._scale, cssClass);
		for (const key in attributes)
			circle.element.attr(key, attributes[key])
		this._elements.push(circle)
		return circle;
	}

	drawLabeledLine(points, label, attributes){
		let element = new LabeledLineElement(this._area, points, label, this._scale);
		for (const key in attributes)
			element.group.setAttribute(key, attributes[key])
		this._elements.push(element)
		return element;
	}

	drawPath(points, cssClass, attributes){
		let element = new PathElement(this._area, points, this._scale, cssClass);
		for (const key in attributes)
			element.element.attr(key, attributes[key])
		this._elements.push(element)
		return element;
	}

	onScaleChanged() {
		for (const element of this._elements) {
			element.onScaleChanged();
		}
	}

	removeElement(el) {
		el.remove();
		this._elements = this._elements.filter(elem => elem !== el);
	}
}

class Axes {
	constructor(axesLayer, scale) {
		this._axisX = d3.select(axesLayer.querySelector('.axis-x'));
		this._axisY = d3.select(axesLayer.querySelector('.axis-y'));
		this._scale = scale;
		this.rescale()
	}

	rescale() {
		const width = this._scale.x.range[1];
		const height = this._scale.y.range[0];
		this.drawHorizontalTicks(width, height)
		this.drawVerticalTicks(width, height)

	}

	drawVerticalTicks(width, height) {
		let ticks = this._scale.y.getTicks(height);
		let fmt = this._scale.y.getFormat();
		this._axisY.call(d3.axisLeft(this._scale.y.d3Scale).tickSize(-width).tickValues(ticks).tickFormat(fmt));
		this._axisY.select('.domain').attr('d', `M0.5,0.5V0.5,${height}`)
		this._axisY.selectAll('.tick title').remove();
		this._axisY.selectAll('.tick').append('title').text((d) => d ? d : "Empty")
	}

	drawHorizontalTicks(width, height) {
		let ticks = this._scale.x.getTicks(width);
		let fmt = this._scale.x.getFormat();
		this._axisX.call(d3.axisBottom(this._scale.x.d3Scale).tickSize(-height).tickValues(ticks).tickFormat(fmt));
		this._axisX.select('.domain').attr('d', `M0.5,0.5H${width},0.5`);
		this._axisX.selectAll('.tick title').remove();
		this._axisX.selectAll('.tick').append('title').text((d) => d ? d : "Empty");
	}
}

class ResizeableChartComponent {
	constructor(parentNode, chartComponent) {
		this._observableNode = parentNode;
		this._chart = chartComponent;

		let timer;
		const resizeObserver = new ResizeObserver(entries => {
			/* w/ delay */ // timer = doWithDelay(timer, () => this.resizeChart(), 500)
			/* w/o delay */ this.resizeChart()
		});
		resizeObserver.observe(this._observableNode);
	}

	resizeChart() {
		let {width, height} = this._observableNode.getBoundingClientRect();
		if (!(width === 0 && height === 0))
			this._chart.resize(width, height);
	}
}

class Drawable {
	constructor() {
		this.element = null;
	}

	onScaleChanged() {
		let attributes = this.positionAttributes;
		for (const name in attributes) {
			this.element.attr(name, attributes[name])
		}
	}

	remove(){
		throw new Error('Not implemented')
	}

	get positionAttributes() {
		throw new Error('Not implemented')
	}
}

class CircleElement extends Drawable {
	constructor(area, coords, scale, cssClass) {
		super();
		this.coords = coords;
		this.scale = scale;
		const {cx, cy} = this.positionAttributes;
		this.element = area.drawCircle(cx, cy, 0, cssClass);
	}

	getBoundingClientRect(){
		return this.element.domElement().getBoundingClientRect();
	}

	get positionAttributes() {
		return {
			cx: this.scale.x.toPx(this.coords.x),
			cy: this.scale.y.toPx(this.coords.y)
		}
	}

	get coordinates(){
		return {
			x: this.element.getAttr('cx'),
			y: this.element.getAttr('cy')
		}
	}

	remove(){
		this.element.remove()
	}
}

class PathElement extends Drawable {
	constructor(area, points, scale, cssClass) {
		super();
		this._points = points;
		this.scale = scale;
		const {d} = this.positionAttributes;
		this.element = area.drawPath(d, cssClass);
		this.dataset = {};
	}

	get positionAttributes() {
		return {
			d: Array.isArray(this._points) && this._points.length > 0
				? d3.line()
					.x(d => this.scale.x.toPx(d[0]))
					.y(d => this.scale.y.toPx(d[1]))
					(this._points)
				: ""
		}
	}
	remove(){
		this.element.remove()
	}
}

export class LabeledLineElement extends Drawable {
	constructor(area, points, label, scale) {
		super();
		this._points = points;
		this._label = label;
		this.scale = scale;
		const {d} = this.positionAttributes;
		this.group = area._createElement('g', 'labeled-line-group');
		const id = randomId();
		this.background = area.drawPath(d, 'labeled-line-background');
		this.element = area.drawPath(d, 'labeled-line');
		this.element.attr('id', id);
		let text = area._createElement('text', 'line-text');
		text.setAttribute('dy', -4);
		let textPath = area._createElement('textPath', 'line-text');
		textPath.setAttribute('href', `#${id}`);
		textPath.setAttribute('startOffset', '100%');
		text.append(textPath)
		this.group.append(this.background.domElement())
		this.group.append(this.element.domElement())
		this.group.append(text)
	}

	onScaleChanged() {
		let attributes = this.positionAttributes;
		for (const name in attributes) {
			this.element.attr(name, attributes[name])
			this.background.attr(name, attributes[name])
		}
	}

	get positionAttributes() {
		return {
			d: Array.isArray(this._points) && this._points.length > 0
				? d3.line()
					.x(d => this._valueToPx(this.scale.x, d[0]))
					.y(d => this._valueToPx(this.scale.y, d[1]))
					(this._points)
				: ""
		}
	}

	set points(points){
		this._points = points;
		const d = this.positionAttributes.d;
		this.background.attr('d', d)
		this.element.attr('d', d)
	}

	set text(text){
		this.group.querySelector('textPath').textContent = text;
	}

	setColor(color){
		this.group.style.color = color;
	}

	remove(){
		this.group.remove()
	}

	_valueToPx(scale, val){
		switch (val) {
			case Number.NEGATIVE_INFINITY: {
				return scale.range[0]
			}
			case Number.POSITIVE_INFINITY: {
				return scale.range.slice(-1)[0]
			}
			default:
				return scale.toPx(val)
		}
	}
}

class PointsSelector {
	#callbacks = {
		onSelectionChanged: [],
	};
	/**
	 * @param {ChartComponent} chart
	 * @param {VizData} vizData
	 * @param {VisualizationDataSelection} selection
	 */
	constructor(chart, vizData, selection) {
		this._chart = chart;
		this._vizData = vizData;
		this._selection = selection;
		selection.addListener(this);
	}

	isSelected(elementId) {
		return this._selection.size() === 0 || this._selection.test(this._vizData.getRowObject(elementId).id);
	}

	selectionChanged(selection, opts = {}) {
		for (const element of this._getCircles()) {
			let el = element.element;
			el.attr('selected', this.isSelected(el.getAttr('id')))
		}
	}

	onSelectionChanged(cb) {
		this.#callbacks.onSelectionChanged.push(cb);
	}

	_onSelectionStarted(selectedArea, event){
		let elements = this._getElementsWithinSelectedArea(selectedArea);
		this.updateSelection(elements, event);
	}

	_onSelecting(selectedArea, event){
		let elements = this._getElementsWithinSelectedArea(selectedArea);
		this.updateSelection(elements, event);
	}

	_onSelectionEnd(){
		const map = new Map();
		for (const element of this._getCircles()) {
			if (element.element.getAttr('selected') === "false")
				continue;
			const id = this._vizData.getRowObject(element.element.getAttr('id')).id.split('-');
			let injectionId = id[0];
			if (!map.has(injectionId))
				map.set(injectionId, new Set())
			if (id.length > 1)
				map.get(injectionId).add(id[1])
		}
		const event = SelectionHelper.createChangeSelectionEvent(map, {});
		this.#callbacks.onSelectionChanged.forEach(cb => cb(event));
	}

	/**
	 *
	 * @param {SvgElement[]} elements
	 * @param {PointerEvent} keys
	 * @private
	 */
	updateSelection(elements, keys) {
		if (keys.shiftKey)
			if (keys.altKey)
				this._removeFromSelection(elements);
			else
				this._addToSelection(elements);
		else
			this._selectExclusively(elements);
	}

	_selectExclusively(elements) {
		this._removeFromSelection(this._getCircles());
		this._addToSelection(elements);
	}

	_addToSelection(elements){
		for (const element of elements){
			element.element.attr('selected', true)
		}
	}

	_removeFromSelection(elements){
		for (const element of elements){
			element.element.attr('selected', false)
		}
	}

	_getElementsWithinSelectedArea(selectionArea) {
		const elements = [];
		for (const element of this._getCircles()) {
			const {x, y} = element.coordinates;
			const isWithin = selectionArea[0][0] <= x && x <= selectionArea[1][0]
				&& selectionArea[0][1] <= y && y <= selectionArea[1][1]
			if (isWithin)
				elements.push(element)
		}
		return elements;
	}

	_getCircles(){
		return this._chart._pointsDrawer._elements.filter(el=>{return el instanceof CircleElement})
	}
}

class ChartPointTooltipComponent {
	constructor(parentNode) {
		this._root = this._render(parentNode);
	}

	show(coords, rows) {
		this._root.querySelector('table').innerHTML = rows.map(row=>`<tr>${row.map(cell=>safeHtml`<td>${cell}</td>`).join('')}</tr>`).join('')
		this._root.style.visibility = 'visible'
		const {x,y} = coords;
		this._root.style.maxWidth = this._root.parentNode.getBoundingClientRect().width - 36 + 'px';
		let rect = this._root.getBoundingClientRect();
		this.moveTo((Math.max(x - rect.width/2, 8)), (y - rect.height - 10));
	}

	moveTo(x, y) {
		this._root.style.left = x + "px";
		this._root.style.top = y + "px";
	}

	hide() {
		this._root.style.visibility = 'hidden'
	}

	_render(parentNode) {
		let tmpl = document.createElement('template');
		tmpl.innerHTML = `<div class="chart-point-tooltip" style="position: fixed">
							<table></table>
						  </div>`
		parentNode.appendChild(tmpl.content)
		return parentNode.lastChild;
	}
}
